import React, { KeyboardEvent, MouseEvent } from 'react';
import {
  Box,
  Checkbox,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowForward, Inventory2Outlined, QrCode2 } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ProductListTableProps, ProductSortBy } from './types';
import { ProductLanguageChips } from './ProductLanguageChips';

const formatDate = (value: string | undefined, locale: string) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
};

const MetadataValue: React.FC<{ value?: string; missingLabel: string; icon: React.ReactNode }> = ({
  value,
  missingLabel,
  icon,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
    {icon}
    <Typography
      variant="body2"
      color={value ? 'text.primary' : 'text.secondary'}
      sx={{
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontStyle: value ? 'normal' : 'italic',
      }}
    >
      {value || missingLabel}
    </Typography>
  </Box>
);

interface SortableHeaderProps {
  label: string;
  sortKey: ProductSortBy;
  activeSortBy: ProductSortBy;
  sortDirection: 'asc' | 'desc';
  onSortChange: (sortBy: ProductSortBy) => void;
}

const SortableHeader: React.FC<SortableHeaderProps> = ({
  label,
  sortKey,
  activeSortBy,
  sortDirection,
  onSortChange,
}) => {
  const isActive = activeSortBy === sortKey;

  return (
    <TableSortLabel
      active={isActive}
      direction={isActive ? sortDirection : 'asc'}
      onClick={() => onSortChange(sortKey)}
      sx={{
        color: 'inherit',
        '&.Mui-active': {
          color: 'primary.dark',
        },
        '& .MuiTableSortLabel-icon': {
          color: 'primary.dark !important',
        },
      }}
    >
      {label}
    </TableSortLabel>
  );
};

export const ProductListTable: React.FC<ProductListTableProps> = ({
  products,
  onView,
  sortBy,
  sortDirection,
  onSortChange,
  selectionMode = false,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onEnterSelectionMode,
}) => {
  const { t, i18n } = useTranslation();
  const missingLabel = t('products.missingValue');

  const selectedCount = selectedIds ? products.filter((p) => selectedIds.has(p.id)).length : 0;
  const allPageSelected = products.length > 0 && selectedCount === products.length;
  const somePageSelected = selectedCount > 0 && selectedCount < products.length;

  const handleHeaderCheckbox = () => {
    if (!selectionMode) {
      // Enter selection mode and select all on this page
      onEnterSelectionMode?.();
      onToggleSelectAll?.(true);
    } else {
      onToggleSelectAll?.(!allPageSelected);
    }
  };

  const handleRowKeyDown = (event: KeyboardEvent<HTMLTableRowElement>, productId: string) => {
    if (selectionMode) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onToggleSelect?.(productId);
      }
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onView(productId);
    }
  };

  const handleRowClick = (productId: string) => {
    if (selectionMode) {
      onToggleSelect?.(productId);
    } else {
      onView(productId);
    }
  };

  const handleActionClick = (event: MouseEvent<HTMLButtonElement>, productId: string) => {
    event.stopPropagation();
    onView(productId);
  };

  const handleRowCheckboxClick = (event: MouseEvent, productId: string) => {
    event.stopPropagation();
    if (!selectionMode) {
      onEnterSelectionMode?.();
    }
    onToggleSelect?.(productId);
  };

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ borderColor: 'divider', overflowX: 'auto' }}
    >
      <Table sx={{ minWidth: 920 }} aria-label={t('products.productListAriaLabel')}>
        <TableHead>
          <TableRow
            sx={{
              '& th': {
                bgcolor: '#f1f5f2',
                color: 'text.secondary',
                fontSize: '0.72rem',
                fontWeight: 700,
                letterSpacing: '0.02em',
                lineHeight: 1.4,
                textTransform: 'uppercase',
                borderBottomColor: 'divider',
              },
            }}
          >
            <TableCell padding="checkbox" sx={{ width: 48 }}>
              <Tooltip title={selectionMode ? t('products.bulk.togglePageSelection', 'Toggle page selection') : t('products.bulk.enterSelectionMode', 'Select products')}>
                <Checkbox
                  size="small"
                  indeterminate={selectionMode && somePageSelected}
                  checked={selectionMode && allPageSelected}
                  onChange={handleHeaderCheckbox}
                  inputProps={{ 'aria-label': 'select all on page' }}
                  sx={{
                    color: selectionMode ? 'primary.main' : 'text.disabled',
                    '&:hover': { color: 'primary.main' },
                    transition: 'color 0.15s ease',
                  }}
                />
              </Tooltip>
            </TableCell>
            <TableCell>{t('products.productName')}</TableCell>
            <TableCell sx={{ width: 190 }}>
              <SortableHeader
                label={t('products.sku')}
                sortKey="sku"
                activeSortBy={sortBy}
                sortDirection={sortDirection}
                onSortChange={onSortChange}
              />
            </TableCell>
            <TableCell sx={{ width: 190 }}>
              <SortableHeader
                label={t('products.ean')}
                sortKey="ean"
                activeSortBy={sortBy}
                sortDirection={sortDirection}
                onSortChange={onSortChange}
              />
            </TableCell>
            <TableCell sx={{ width: 230 }}>{t('products.translations')}</TableCell>
            <TableCell sx={{ width: 150 }}>
              <SortableHeader
                label={t('products.updated')}
                sortKey="updated_at"
                activeSortBy={sortBy}
                sortDirection={sortDirection}
                onSortChange={onSortChange}
              />
            </TableCell>
            <TableCell align="right" sx={{ width: 72 }}>
              {t('products.actions')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {products.map((product) => {
            const updatedAt = formatDate(product.updated_at, i18n.language) || missingLabel;
            const isSelected = selectedIds?.has(product.id) ?? false;

            return (
              <TableRow
                key={product.id}
                hover
                selected={isSelected}
                tabIndex={0}
                onClick={() => handleRowClick(product.id)}
                onKeyDown={(event) => handleRowKeyDown(event, product.id)}
                sx={{
                  cursor: 'pointer',
                  transition: 'background-color 0.16s ease',
                  '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: -2,
                  },
                  '& td': {
                    py: 1.6,
                    borderBottomColor: 'divider',
                  },
                }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={isSelected}
                    onClick={(e) => handleRowCheckboxClick(e, product.id)}
                    inputProps={{ 'aria-label': `select ${product.name}` }}
                    sx={{
                      opacity: selectionMode ? 1 : 0,
                      'tr:hover &': { opacity: 1 },
                      transition: 'opacity 0.15s ease',
                      color: isSelected ? 'primary.main' : 'text.disabled',
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        color: 'text.primary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: 360,
                      }}
                    >
                      {product.name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <MetadataValue
                    value={product.sku}
                    missingLabel={missingLabel}
                    icon={<Inventory2Outlined sx={{ fontSize: 16, color: 'text.secondary' }} />}
                  />
                </TableCell>
                <TableCell>
                  <MetadataValue
                    value={product.ean}
                    missingLabel={missingLabel}
                    icon={<QrCode2 sx={{ fontSize: 16, color: 'text.secondary' }} />}
                  />
                </TableCell>
                <TableCell>
                  <ProductLanguageChips
                    languages={product.translation_languages}
                    baseLanguage={product.base_language}
                    maxVisible={4}
                  />
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    color={updatedAt === missingLabel ? 'text.secondary' : 'text.primary'}
                    sx={{ fontStyle: updatedAt === missingLabel ? 'italic' : 'normal' }}
                  >
                    {updatedAt}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={t('products.openProduct')}>
                    <IconButton
                      size="small"
                      aria-label={t('products.openProductNamed', { name: product.name })}
                      onClick={(event) => handleActionClick(event, product.id)}
                      sx={{
                        color: 'primary.dark',
                        border: '1px solid',
                        borderColor: 'divider',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: '#edf7ef',
                        },
                      }}
                    >
                      <ArrowForward fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
